import React, { useEffect, useState } from "react";
import axios from "axios";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import API_BASE_URL from "../config";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "./CalendarPage.css";

const localizer = momentLocalizer(moment);

const CalendarPage = () => {
  const [events, setEvents] = useState([]);
  const [selectedDateEvents, setSelectedDateEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [newEventTitle, setNewEventTitle] = useState("");
  const [editEvent, setEditEvent] = useState(null);
  const [clickedDate, setClickedDate] = useState(null);

  // Fetch all events + birthdays + interviews
  useEffect(() => {
    const fetchAllEvents = async () => {
      try {
        setLoading(true);

        // Company events
        const eventRes = await axios.get(`${API_BASE_URL}/api/events`);
        const eventData = Array.isArray(eventRes.data)
          ? eventRes.data.map((e) => ({
              id: e._id,
              title: e.title,
              start: new Date(e.date),
              end: new Date(e.date),
              type: e.type,
            }))
          : [];

        // Employee birthdays
        const personalRes = await axios.get(`${API_BASE_URL}/api/personal-info`);
        const birthdayEvents = (personalRes.data?.data || [])
          .map((p) => {
            if (!p.dateOfBirth || !p.employeeId?.name) return null;
            const dob = new Date(p.dateOfBirth);
            const currentYear = new Date().getFullYear();
            const birthdayDate = new Date(currentYear, dob.getMonth(), dob.getDate());
            return {
              id: `birthday-${p._id}`,
              title: `🎂 ${p.employeeId.name}'s Birthday`,
              start: birthdayDate,
              end: birthdayDate,
              type: "birthday",
            };
          })
          .filter(Boolean);

        // Scheduled interviews
        const interviewRes = await axios.get(`${API_BASE_URL}/api/candidates/interviews`);
        const interviewEvents = (interviewRes.data?.data || [])
          .map((interview) => {
            if (!interview.interviewDate) return null;
            return {
              id: interview._id,
              title: `📋 Interview: ${interview.candidateName || 'Candidate'}`,
              start: new Date(interview.interviewDate),
              end: new Date(interview.interviewDate),
              type: "interview",
            };
          })
          .filter(Boolean);

        setEvents([...eventData, ...birthdayEvents, ...interviewEvents]);
      } catch (err) {
        console.error("Error fetching events:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAllEvents();
  }, []);

  // Click a date to show events or add new
  const handleSelectSlot = ({ start }) => {
    setClickedDate(start);
    const dateEvents = events.filter(
      (e) => moment(e.start).format("YYYY-MM-DD") === moment(start).format("YYYY-MM-DD")
    );
    setSelectedDateEvents(dateEvents);
    setShowModal(true);
  };

  // Click event to edit
  const handleSelectEvent = (event) => {
    // Only allow editing for company events (not birthdays or interviews)
    if (event.type === "event") {
      setEditEvent(event);
      setNewEventTitle(event.title);
      setShowEditModal(true);
    }
  };

  // Save new event
  const handleAddEvent = async () => {
    if (!newEventTitle || !clickedDate) return;
    try {
      const res = await axios.post(`${API_BASE_URL}/api/events`, {
        title: newEventTitle,
        date: clickedDate,
      });

      const newEvent = {
        id: res.data._id,
        title: res.data.title,
        start: new Date(res.data.date),
        end: new Date(res.data.date),
        type: res.data.type || "event",
      };

      // Add to state
      setEvents((prev) => [...prev, newEvent]);

      // Update selected date events immediately
      if (moment(newEvent.start).format("YYYY-MM-DD") === moment(clickedDate).format("YYYY-MM-DD")) {
        setSelectedDateEvents((prev) => [...prev, newEvent]);
      }

      setNewEventTitle("");
      setShowModal(false);
    } catch (err) {
      console.error("Error adding event:", err);
    }
  };

  // Update event
  const handleUpdateEvent = async () => {
    if (!newEventTitle || !editEvent) return;
    try {
      const res = await axios.put(`${API_BASE_URL}/api/events/${editEvent.id}`, {
        title: newEventTitle,
        date: editEvent.start,
      });

      const updatedEvent = {
        ...editEvent,
        title: newEventTitle,
      };

      // Update in state
      setEvents((prev) =>
        prev.map((event) => (event.id === editEvent.id ? updatedEvent : event))
      );

      // Update selected date events if visible
      setSelectedDateEvents((prev) =>
        prev.map((event) => (event.id === editEvent.id ? updatedEvent : event))
      );

      setNewEventTitle("");
      setEditEvent(null);
      setShowEditModal(false);
    } catch (err) {
      console.error("Error updating event:", err);
    }
  };

  // Delete event
  const handleDeleteEvent = async (eventId, eventType) => {
    // Prevent deletion of birthdays and interviews
    if (eventType !== "event") {
      alert("Cannot delete birthdays or interviews from calendar");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this event?")) return;

    try {
      await axios.delete(`${API_BASE_URL}/api/events/${eventId}`);

      // Remove from state
      setEvents((prev) => prev.filter((event) => event.id !== eventId));

      // Remove from selected date events if visible
      setSelectedDateEvents((prev) =>
        prev.filter((event) => event.id !== eventId)
      );

      alert("Event deleted successfully!");
    } catch (err) {
      console.error("Error deleting event:", err);
      alert("Error deleting event. Please try again.");
    }
  };

  // Close all modals
  const handleCloseModals = () => {
    setShowModal(false);
    setShowEditModal(false);
    setNewEventTitle("");
    setEditEvent(null);
  };

  return (
    <div className="calendar-page">
      <h2 className="calendar-title">🎉 Company Calendar</h2>

      {loading ? (
        <div className="loading-message">Loading calendar data...</div>
      ) : (
        <div className="calendar-container">
          <div className="calendar-main">
            <Calendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              style={{ height: 600 }}
              selectable
              onSelectSlot={handleSelectSlot}
              onSelectEvent={handleSelectEvent}
              popup
            />
          </div>

          <div className="calendar-sidebar">
            <h3>Events on selected date:</h3>
            {selectedDateEvents.length === 0 ? (
              <p>No events</p>
            ) : (
              <ul>
                {selectedDateEvents.map((e) => (
                  <li key={e.id} className="event-item">
                    <div className="event-content">
                      <strong>{e.title}</strong> ({e.type})
                    </div>
                    <div className="event-actions">
                      {/* Only show edit/delete for company events */}
                      {e.type === "event" && (
                        <>
                          <button
                            className="edit-btn"
                            onClick={() => {
                              setEditEvent(e);
                              setNewEventTitle(e.title);
                              setShowEditModal(true);
                            }}
                            title="Edit event"
                          >
                            ✏️
                          </button>
                          <button
                            className="delete-btn"
                            onClick={() => handleDeleteEvent(e.id, e.type)}
                            title="Delete event"
                          >
                            🗑️
                          </button>
                        </>
                      )}
                      {/* Show info for non-editable events */}
                      {(e.type === "birthday" || e.type === "interview") && (
                        <span className="info-text">System Event</span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Add Event Modal */}
      {showModal && (
        <div className="modal-backdrop">
          <div className="modal">
            <h3>Add Event on {moment(clickedDate).format("DD MMM YYYY")}</h3>
            <input
              type="text"
              placeholder="Event title"
              value={newEventTitle}
              onChange={(e) => setNewEventTitle(e.target.value)}
            />
            <div className="modal-actions">
              <button onClick={handleAddEvent}>Add Event</button>
              <button onClick={handleCloseModals}>Close</button>
            </div>

            {selectedDateEvents.length > 0 && (
              <>
                <h4>Existing Events:</h4>
                <ul>
                  {selectedDateEvents.map((e) => (
                    <li key={e.id}>
                      <strong>{e.title}</strong> ({e.type})
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>
      )}

      {/* Edit Event Modal */}
      {showEditModal && editEvent && (
        <div className="modal-backdrop">
          <div className="modal">
            <h3>Edit Event on {moment(editEvent.start).format("DD MMM YYYY")}</h3>
            <input
              type="text"
              placeholder="Event title"
              value={newEventTitle}
              onChange={(e) => setNewEventTitle(e.target.value)}
            />
            <div className="modal-actions">
              <button onClick={handleUpdateEvent}>Update Event</button>
              <button 
                className="delete-btn"
                onClick={() => handleDeleteEvent(editEvent.id, editEvent.type)}
              >
                Delete Event
              </button>
              <button onClick={handleCloseModals}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarPage;